import {
  Box,
  Chip,
  Dialog,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import AppRegistrationIcon from '@mui/icons-material/AppRegistration';
import AppsIcon from '@mui/icons-material/Apps';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CreateIcon from '@mui/icons-material/Create';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import DevicesIcon from '@mui/icons-material/Devices';
import GroupsIcon from '@mui/icons-material/Groups';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import SearchIcon from '@mui/icons-material/Search';
import SimCardIcon from '@mui/icons-material/SimCard';
import SummarizeIcon from '@mui/icons-material/Summarize';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import TuneIcon from '@mui/icons-material/Tune';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

const NAV_COMMANDS = [
  { label: 'Devices', path: '/dashboard/devices', icon: <DevicesIcon />, keywords: 'fiscal pos terminals' },
  { label: 'Companies', path: '/dashboard/companies', icon: <LocationCityIcon />, keywords: 'organization tenant' },
  { label: 'Merchants', path: '/dashboard/merchants', icon: <PointOfSaleIcon />, keywords: 'store retailer' },
  { label: 'Applications', path: '/dashboard/apps', icon: <AppsIcon />, keywords: 'apk apps software' },
  { label: 'Repositories', path: '/dashboard/repositories', icon: <AppRegistrationIcon />, keywords: 'repo bundles' },
  { label: 'Parameters', path: '/dashboard/parameters', icon: <TuneIcon />, keywords: 'config configuration' },
  { label: 'SIMs', path: '/dashboard/sim', icon: <SimCardIcon />, keywords: 'sim cards' },
  { label: 'Banks', path: '/dashboard/banks', icon: <AccountBalanceIcon />, keywords: 'banking' },
  { label: 'Bank Device Records', path: '/dashboard/bankdevices', icon: <AccountBalanceIcon />, keywords: 'bank device registry' },
  { label: 'Incidents', path: '/dashboard/incidents', icon: <SummarizeIcon />, keywords: 'tickets issues' },
  { label: 'Incident Types', path: '/dashboard/incidenttype', icon: <SummarizeIcon />, keywords: 'categories' },
  { label: 'SLA Times', path: '/dashboard/companySLAtime', icon: <TimelapseIcon />, keywords: 'sla time' },
  { label: 'Reports — Incidents', path: '/dashboard/reports', icon: <AssessmentIcon />, keywords: 'incident report' },
  { label: 'Reports — SLA', path: '/dashboard/reports/sla-reports', icon: <AssessmentIcon />, keywords: 'sla report' },
  { label: 'Reports — Invoicing', path: '/dashboard/reports/invoicing-reports', icon: <AssessmentIcon />, keywords: 'billing invoice' },
  { label: 'Model Types', path: '/dashboard/modeltypes', icon: <DeviceHubIcon />, keywords: 'hardware models' },
  { label: 'Users', path: '/dashboard/users', icon: <ManageAccountsIcon />, keywords: 'accounts people' },
  { label: 'Roles', path: '/dashboard/roles', icon: <GroupsIcon />, keywords: 'permissions groups' },
  { label: 'Logs — Audit', path: '/dashboard/logs/auditlog', icon: <CreateIcon />, keywords: 'audit trail' },
  { label: 'Logs — Sync', path: '/dashboard/logs/sync', icon: <CreateIcon />, keywords: 'device sync mqtt' },
  { label: 'Logs — API', path: '/dashboard/logs/api', icon: <CreateIcon />, keywords: 'api integration' },
  { label: 'Logs — User', path: '/dashboard/logs/userlog', icon: <CreateIcon />, keywords: 'user activity' },
  { label: 'Logs — Device Apps', path: '/dashboard/logs/deviceapplicationlog', icon: <CreateIcon />, keywords: 'install uninstall' },
  { label: 'Logs — Device Params', path: '/dashboard/logs/deviceparameterlog', icon: <CreateIcon />, keywords: 'parameter changes' },
];

const score = (cmd, q) => {
  if (!q) return 1;
  const haystack = `${cmd.label} ${cmd.keywords || ''}`.toLowerCase();
  const needle = q.toLowerCase();
  if (haystack.startsWith(needle)) return 3;
  if (cmd.label.toLowerCase().includes(needle)) return 2;
  if (haystack.includes(needle)) return 1;
  return 0;
};

const CommandPalette = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHighlight(0);
      const focusTimeout = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(focusTimeout);
    }
  }, [open]);

  const results = useMemo(() => {
    return NAV_COMMANDS
      .map((c) => ({ ...c, _score: score(c, query.trim()) }))
      .filter((c) => c._score > 0)
      .sort((a, b) => b._score - a._score)
      .slice(0, 12);
  }, [query]);

  useEffect(() => {
    if (highlight >= results.length) setHighlight(0);
  }, [results.length, highlight]);

  const runCommand = (cmd) => {
    setOpen(false);
    navigate(cmd.path);
  };

  const onListKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => (h + 1) % Math.max(results.length, 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => (h - 1 + results.length) % Math.max(results.length, 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[highlight]) runCommand(results[highlight]);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { position: 'absolute', top: '10vh', m: 0, overflow: 'hidden' },
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          inputRef={inputRef}
          variant="standard"
          placeholder="Search or jump to…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={onListKeyDown}
          InputProps={{
            disableUnderline: true,
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {results.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No commands match "{query}".
            </Typography>
          </Box>
        ) : (
          <List dense>
            {results.map((cmd, idx) => (
              <ListItem
                button
                key={cmd.path}
                selected={idx === highlight}
                onClick={() => runCommand(cmd)}
                onMouseEnter={() => setHighlight(idx)}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'text.secondary' }}>
                  {cmd.icon}
                </ListItemIcon>
                <ListItemText primary={cmd.label} />
                {idx === highlight && (
                  <Chip
                    size="small"
                    label="Enter"
                    variant="outlined"
                    sx={{ ml: 1, fontFamily: 'monospace', fontSize: 11 }}
                  />
                )}
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <Box
        sx={{
          px: 2,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: 'text.secondary',
        }}
      >
        <span>↑↓ to navigate · Enter to select</span>
        <span>⌘K or Ctrl+K to toggle</span>
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
